import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, LeafIcon, ShieldIcon, CheckIcon, StarIcon, SparkleIcon } from './icons';
const headlineWords = ['How', 'To', 'Add', {
  text: '5+ Healthy Years',
  highlight: true
}, 'To', 'Your', "Dog's", 'Life', 'in', 'Just', {
  text: '10 Minutes',
  highlight: true
}, 'a', 'Day'];
export const Hero: React.FC = () => {
  return <section className="relative pt-10 pb-20 px-6 sm:px-8 lg:px-12 max-w-6xl mx-auto overflow-hidden" data-id="element-61">
      {/* Decorative floating sparkles */}
      <motion.div animate={{
      y: [0, -10, 0],
      opacity: [0.4, 0.8, 0.4]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-20 left-4 text-gold/40 hidden lg:block" data-id="element-62">
        <SparkleIcon className="w-6 h-6" data-id="element-63" />
      </motion.div>
      <motion.div animate={{
      y: [0, -8, 0],
      opacity: [0.3, 0.7, 0.3]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-32 right-8 text-gold/40 hidden lg:block" data-id="element-64">
        <SparkleIcon className="w-8 h-8" data-id="element-65" />
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-12 items-center" data-id="element-66">
        <div className="flex-1 text-center lg:text-left" data-id="element-67">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="inline-flex items-center gap-2 mb-5 bg-forest/5 border border-forest/10 rounded-full px-3 py-1.5" data-id="element-68">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" data-id="element-69" />
            <span className="text-[10px] sm:text-xs font-bold tracking-[0.2em] text-forest-dark uppercase" data-id="element-70">
              The Longevity Protocol · 2026 Edition
            </span>
          </motion.div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest-dark leading-[1.05] mb-6" data-id="element-71">
            {headlineWords.map((word, i) => {
            const isObj = typeof word === 'object';
            const text = isObj ? word.text : word;
            const highlight = isObj && word.highlight;
            return <motion.span key={i} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3 + i * 0.06,
              ease: 'easeOut'
            }} className={`inline-block mr-[0.25em] ${highlight ? 'italic text-gold-dark relative' : ''}`} data-id="element-72">
                  {text}
                  {highlight && <motion.span initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 0.6,
                delay: 0.3 + i * 0.06 + 0.4,
                ease: 'easeOut'
              }} className="absolute left-0 right-0 bottom-0 h-[3px] bg-gold/50 origin-left" data-id="element-73" />}
                </motion.span>;
          })}
          </h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 1.4
        }} className="text-lg sm:text-xl text-charcoal/80 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-74">
            Without expensive subscriptions or complex "Raw Feeding" science —
            even if your dog is already a senior or currently struggling with
            low energy.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 1.5
        }} className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-5 mb-8 text-xs sm:text-sm" data-id="element-75">
            <div className="flex items-center gap-1.5" data-id="element-76">
              <div className="flex gap-0.5" data-id="element-77">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3.5 h-3.5 text-gold" data-id="element-78" />)}
              </div>
              <span className="font-bold text-charcoal" data-id="element-79">4.9</span>
              <span className="text-charcoal/60" data-id="element-80">/ 2,431 reviews</span>
            </div>
            <span className="h-4 w-px bg-gold/40 hidden sm:block" data-id="element-81" />
            <div className="flex items-center gap-1.5" data-id="element-82">
              <span className="relative flex h-2 w-2" data-id="element-83">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" data-id="element-84"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" data-id="element-85"></span>
              </span>
              <span className="font-bold text-charcoal" data-id="element-86">12,847+</span>
              <span className="text-charcoal/60" data-id="element-87">dogs already saved</span>
            </div>
            <span className="h-4 w-px bg-gold/40 hidden sm:block" data-id="element-88" />
            <div className="flex items-center gap-1.5 bg-forest-dark text-gold font-bold text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded" data-id="element-89">
              #1 Bestseller
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 1.5
        }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8" data-id="element-90">
            <a href="#final-cta" className="relative w-full sm:w-auto bg-forest hover:bg-forest-dark text-cream font-bold text-lg px-8 py-4 rounded-lg shadow-lg transition-all hover:-translate-y-0.5 group overflow-hidden inline-flex items-center justify-center" data-id="element-91">
              <span className="absolute inset-0 bg-gold/0 group-hover:bg-gold/10 transition-colors" data-id="element-92" />
              <span className="relative flex items-center justify-center gap-2" data-id="element-93">
                GET INSTANT ACCESS FOR $6.39
              </span>
              <motion.span animate={{
              x: ['-100%', '200%']
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 1
            }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-cream/20 to-transparent pointer-events-none" data-id="element-94" />
            </a>
            <div className="flex flex-col items-center sm:items-start" data-id="element-95">
              <span className="text-sm text-charcoal/80 font-bold" data-id="element-96">
                <span className="line-through text-charcoal/40 mr-1.5" data-id="element-97">
                  $557
                </span>
                <span className="text-gold-dark" data-id="element-98">$6.39 Today</span>
              </span>
              <span className="text-xs text-charcoal/60" data-id="element-99">
                Instant digital access
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 1.7
        }} className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-gold/30" data-id="element-100">
            {[{
            Icon: ClockIcon,
            label: '10-Min Daily'
          }, {
            Icon: LeafIcon,
            label: 'Grocery Foods'
          }, {
            Icon: CheckIcon,
            label: 'Vet-Reviewed'
          }, {
            Icon: ShieldIcon,
            label: '60-Day Guarantee'
          }].map(({
            Icon,
            label
          }) => <div key={label} className="flex flex-col items-center lg:items-start gap-2" data-id="element-101">
                <Icon className="w-5 h-5 text-gold" data-id="element-102" />
                <span className="text-xs font-semibold uppercase tracking-wider text-charcoal/70" data-id="element-103">
                  {label}
                </span>
              </div>)}
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="flex-1 w-full max-w-md lg:max-w-none relative" data-id="element-104">
          <div className="absolute inset-0 bg-gold/20 rounded-2xl translate-x-3 translate-y-3" data-id="element-105" />
          <img src="https://cdn.magicpatterns.com/uploads/f8ZzbBMdJzaJKcu29Yrrug/hero_2.png" alt="Cane Corso standing beside a bowl of fresh salmon, blueberries and turmeric in a modern kitchen" className="relative z-10 w-full h-auto rounded-2xl shadow-xl border border-gold/30 object-cover aspect-[4/5] sm:aspect-square" data-id="element-106" />

          {/* Floating testimonial card */}
          <motion.div initial={{
          opacity: 0,
          y: 20,
          x: 20
        }} animate={{
          opacity: 1,
          y: 0,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 1.2
        }} className="hidden sm:flex absolute -bottom-6 -left-4 z-20 bg-white rounded-xl shadow-2xl border border-gold/30 p-3 max-w-[240px] items-start gap-3" data-id="element-107">
            <div className="bg-gold/10 rounded-full p-2 shrink-0" data-id="element-108">
              <CheckIcon className="w-4 h-4 text-gold-dark" data-id="element-109" />
            </div>
            <div data-id="element-110">
              <div className="flex gap-0.5 mb-1" data-id="element-111">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3 h-3 text-gold" data-id="element-112" />)}
              </div>
              <p className="text-xs text-charcoal/80 italic leading-snug" data-id="element-113">
                "He has the energy of a puppy again!"
              </p>
              <p className="text-[10px] text-charcoal/50 mt-1 uppercase tracking-wider" data-id="element-114">
                — Sarah, TX
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2
    }} className="hidden lg:flex flex-col items-center gap-2 mt-12 text-charcoal/40" data-id="element-115">
        <span className="text-[10px] uppercase tracking-[0.3em] font-semibold" data-id="element-116">
          Discover the protocol
        </span>
        <motion.div animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="w-px h-8 bg-gold/40" data-id="element-117" />
      </motion.div>
    </section>;
};